import * as React from "react"
import { format } from "date-fns"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

interface DatePickerProps {
  value?: string // yyyy-MM-dd
  onChange: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  disabled?: boolean
  className?: string
  triggerRef?: React.Ref<HTMLButtonElement>
  id?: string
  dataField?: string
}

function parseLocalDate(value?: string) {
  if (!value) return undefined
  // IMPORTANT: treat date-only strings as local dates (avoid timezone shifting)
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number)
    return new Date(y, m - 1, d)
  }
  const dt = new Date(value)
  return Number.isNaN(dt.getTime()) ? undefined : dt
}

/**
 * Canonical DatePicker for the marketing website (mirrors `frontend/` UX)
 * - month/year dropdowns
 * - emits ISO yyyy-MM-dd string
 * - displays dd-MMM-yyyy
 */
export function DatePicker({
  value,
  onChange,
  onBlur,
  placeholder = "Pick a date",
  disabled = false,
  className,
  triggerRef,
  id,
  dataField,
}: DatePickerProps) {
  const date = parseLocalDate(value)
  const [open, setOpen] = React.useState(false)

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      onChange(format(selectedDate, "yyyy-MM-dd"))
    } else {
      onChange("")
    }
    onBlur?.()
    setOpen(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) onBlur?.()
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          id={id}
          data-field={dataField}
          data-empty={!date}
          disabled={disabled}
          className={cn(
            "data-[empty=true]:text-muted-foreground w-full justify-between text-left font-normal",
            className
          )}
          ref={triggerRef as any}
        >
          {date ? format(date, "dd-MMM-yyyy") : <span>{placeholder}</span>}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          captionLayout="dropdown"
          fromYear={1950}
          toYear={2100}
          initialFocus
          onSelect={handleDateSelect}
        />
      </PopoverContent>
    </Popover>
  )
}

